import { useMemo } from 'react';
import { IRccapReportDetail } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { DropdownButton, Label, MenuItem } from 'react-bootstrap';
import { ReportWrapper } from './styles';
import OverlayMenuButton, { IMenuItem } from '@nkr/components/overlay-menu-button';

interface IProps {
    data: IRccapReportDetail[],
    onGridReady: (e: GridReadyEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void,
    onRowDataChanged: (e: RowDataChangedEvent) => void,
    onSortChanged: (e: SortChangedEvent) => void,
    onAction: (id: number, action: any) => void
}

const Grid = (props: IProps) => {
    const cols: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'Rank',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            width: 50,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'failDate',
            field: 'failDate',
            headerName: 'Fail Date',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Fail Date</SortableHeaderComponent>,
            width: 90,
        },
        {
            colId: 'responseDate',
            field: 'responseDate',
            headerName: 'Response Date',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Response<br />Date</SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            headerClass: 'list-header',
            width: 170,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor<br />Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRccapReportDetail;

                const donorIsResponsible = data.responsibleCenterId == data.donorCenterId
                    || (data.rdnCenterId > 0 && data.responsibleCenterId == data.rdnCenterId);

                if (donorIsResponsible) {
                    return (
                        <>
                            {data.rdnCenterId > 0
                                ? <>{data.donorCenter}&nbsp;(<Label bsStyle='warning' style={{ color: '#333', fontSize: '100%' }}>{data.rdnCenter}</Label>)</>
                                : <Label bsStyle='warning' style={{ color: '#333', fontSize: '100%' }}>{data.donorCenter}</Label>
                            }
                        </>
                    );
                }

                if (data.rdnCenterId > 0) {
                    return (
                        <>{data.donorCenter} (<span style={{ color: 'red' }}>{data.rdnCenter}</span>)</>
                    );
                }

                return (
                    <>{data.donorCenter}</>
                );
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IRccapReportDetail;

                if (data.rdnCenterId > 0) {
                    return data.donorCenter + ' (' + data.rdnCenter + ')';
                } else {
                    return data.donorCenter;
                }
            }
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient<br />Center</SortableHeaderComponent>,
            width: 145,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRccapReportDetail;

                if (data.responsibleCenterId == data.recipientCenterId) {
                    return <Label bsStyle='warning' style={{ color: '#333', fontSize: '100%' }}>{data.recipientCenter}</Label>;
                }

                return <>{data.recipientCenter}</>
            }
        },
        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'Swap #',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap #</SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'swapManager',
            field: 'swapManager',
            headerName: 'Mgr',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap<br />Mgr</SortableHeaderComponent>,
            width: 80,
        },
        {
            colId: 'status',
            field: 'status',
            headerName: 'Status',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Status</SortableHeaderComponent>,
            width: 130,
        },
        {
            colId: 'transplants',
            field: 'transplants',
            headerName: 'TXPs',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXPs</SortableHeaderComponent>,
            width: 60,
        },
        {
            colId: 'type',
            field: 'type',
            headerName: 'Type',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Type</SortableHeaderComponent>,
            width: 255,
        },
        {
            colId: 'billable',
            field: 'billable',
            headerName: 'Billable',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Billable</SortableHeaderComponent>,
            width: 95,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRccapReportDetail;

                if (data.billable == 1) {
                    return <Label bsStyle='warning' style={{ color: '#333', fontSize: '100%' }}>Yes</Label>;
                }

                if (data.billable == 2) {
                    return <Label bsStyle='success' style={{ fontSize: '100%' }}>No</Label>;
                }

                return <Label style={{ fontSize: '100%' }}>Unknown</Label>;
            }
        },
        {
            colId: 'id',
            field: 'id',
            headerName: '',
            headerClass: 'list-header',
            width: 80,
            cellClass: 'action-menu',
            cellRendererParams: { onAction: props.onAction },
            cellRendererFramework: (params: ICellRendererParams & { onAction: (id: number, action: any) => void }) => {
                let data = params.data as IRccapReportDetail;
                let menu: IMenuItem[] = [
                    {
                        id: data.id,
                        action: 0,
                        include: true,
                        selectable: true,
                        element: <>Edit</>
                    },
                    {
                        id: data.id,
                        action: 1,
                        include: true,
                        selectable: true,
                        element: <>Delete</>
                    },
                    {
                        id: data.id,
                        action: 2,
                        include: true,
                        selectable: true,
                        element: <>PDF</>
                    }
                ];

                return (
                    <OverlayMenuButton
                    menu={menu}
                    bsStyle='primary'
                    bsSize='xs'
                    title='Actions'
                    listItemStyle={{ padding: '2px 10px', fontSize: 14 }}
                    includeCaret={true}                    
                    onSelect={(id, action) => props.onAction(id, action)}
                    />
                )            
            }
        }
    ]

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false,
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={45}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                onGridReady={props.onGridReady}
                onFirstDataRendered={props.onFirstDataRendered}
                onRowDataChanged={props.onRowDataChanged}
                onSortChanged={props.onSortChanged}
            />
        </ReportWrapper>
    );
}

export default Grid;